import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Tabs, Tab, Alert, ButtonGroup, Accordion } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';

const TemplateEditor = ({
  show,
  onHide,
  onSave,
  initialTemplate = '',
  templateType = 'default',
  offerData = null,
  projectData = null,
  projectItems = [],
  customerData = null,
  contractorData = null
}) => {
  const { t } = useTranslation();
  const [templateContent, setTemplateContent] = useState(initialTemplate);
  const [activeTab, setActiveTab] = useState('wysiwyg');
  const [error, setError] = useState(null);
  const [editorMode, setEditorMode] = useState('wysiwyg'); // 'wysiwyg' or 'code'
  const [showComponentLibrary, setShowComponentLibrary] = useState(true);
  const [renderJinja, setRenderJinja] = useState(true); // Toggle for rendering Jinja2 variables
  const editorRef = useRef(null);

  // Undo/Redo history management
  const [history, setHistory] = useState([initialTemplate]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isUndoRedoAction, setIsUndoRedoAction] = useState(false);
  const [isDragDropAction, setIsDragDropAction] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Use REAL data if available, otherwise fallback to sample data
  const sampleData = {
    offer: {
      name: offerData?.name || t('projects.offers.defaultName'),
      id: offerData?.id || 123,
      date_issued: offerData?.date_issued || '2024-06-15',
      offer_valid_until: offerData?.offer_valid_until || '2024-07-31',
      version: offerData?.version || '1.0',
      discount: offerData?.discount || 10,
      note: offerData?.note || 'Sleva 10% při objednávce do konce měsíce',
      description: offerData?.description || 'Kompletní pronájem techniky pro venkovní akci',
      color: '#4CAF50',
      include_items: offerData?.include_items !== false,
      hide_prices: offerData?.hide_prices || false,
      include_customer_info: offerData?.include_customer_info !== false,
      include_vat: offerData?.include_vat !== false,
      customer: {
        company_name: customerData?.company_name || 'Festival Events s.r.o.',
        resp_person: customerData?.resp_person || 'Jan Novák',
        email: customerData?.email || 'jan.novak@festival.cz',
        phone: customerData?.phone || '+420 777 888 999',
        street: customerData?.street || 'Hlavní 123',
        city: customerData?.city || 'Praha',
        postal_code: customerData?.postal_code || '110 00',
        company_id: customerData?.company_id || '12345678',
        tax_id: customerData?.tax_id || 'CZ12345678'
      },
      contractor: {
        display_name: contractorData?.display_name || contractorData?.first_name + ' ' + contractorData?.last_name || 'Petr Svoboda',
        company: {
          company_name: contractorData?.company?.company_name || 'Pro Audio s.r.o.',
          company_clr: '#0066cc'
        }
      },
      project: {
        name: projectData?.name || 'Letní festival 2024',
        location: projectData?.location || 'Praha - Letná',
        start_date: projectData?.start_date || '2024-08-01',
        end_date: projectData?.end_date || '2024-08-03'
      }
    },
    project: {
      id: projectData?.id || 456,
      name: projectData?.name || 'Letní festival 2024',
      location: projectData?.location || 'Praha - Letná',
      start_date: projectData?.start_date || '2024-08-01',
      end_date: projectData?.end_date || '2024-08-03',
      tax_rate: projectData?.tax_rate || 21
    },
    subtotal: '125000.00',
    discount_amount: '12500.00',
    vat_amount: '26250.00',
    total: '151250.00',
    item_total_without_tax: '95000.00',
    vehicle_total_without_tax: '15000.00',
    staff_total_without_tax: '15000.00',
    project_items: projectItems && projectItems.length > 0
      ? projectItems.map(item => ({
          name: item.item_name || item.name || 'Item',
          quantity: item.quantity || 1,
          time_units: item.time_units || 1,
          rate_type: item.rate_type || 'days',
          price_rent: item.price_rent || '0',
          price: item.price || '0',
          category: item.category || 'General'
        }))
      : [
          { name: 'LED Panel 500x500', quantity: 20, time_units: 3, rate_type: 'days', price_rent: '500', price: '30000', category: 'Osvětlení' },
          { name: 'Moving Head Spot', quantity: 10, time_units: 3, rate_type: 'days', price_rent: '800', price: '24000', category: 'Osvětlení' },
          { name: 'Aktivní reprobox 15"', quantity: 8, time_units: 3, rate_type: 'days', price_rent: '600', price: '14400', category: 'Zvuk' }
        ]
  };

  // Render Jinja2 variables with actual data
  const renderJinjaVariables = (html) => {
    if (!renderJinja) return html;

    let rendered = html;

    // First decode any HTML entities that might have been encoded
    const textarea = document.createElement('textarea');
    textarea.innerHTML = rendered;
    rendered = textarea.value;

    // Simple variable replacement {{ variable }}
    rendered = rendered.replace(/\{\{\s*offer\.name\s*\}\}/g, sampleData.offer.name);
    rendered = rendered.replace(/\{\{\s*offer\.id\s*\}\}/g, sampleData.offer.id);
    rendered = rendered.replace(/\{\{\s*offer\.date_issued\s*\}\}/g, sampleData.offer.date_issued);
    rendered = rendered.replace(/\{\{\s*offer\.offer_valid_until\s*\}\}/g, sampleData.offer.offer_valid_until);
    rendered = rendered.replace(/\{\{\s*offer\.version\s*\}\}/g, sampleData.offer.version);
    rendered = rendered.replace(/\{\{\s*offer\.discount\s*\}\}/g, sampleData.offer.discount);
    rendered = rendered.replace(/\{\{\s*offer\.note\s*\}\}/g, sampleData.offer.note);
    rendered = rendered.replace(/\{\{\s*offer\.description\s*\}\}/g, sampleData.offer.description);
    rendered = rendered.replace(/\{\{\s*offer\.customer\.company_name\s*\}\}/g, sampleData.offer.customer.company_name);
    rendered = rendered.replace(/\{\{\s*offer\.customer\.resp_person\s*\}\}/g, sampleData.offer.customer.resp_person);
    rendered = rendered.replace(/\{\{\s*offer\.customer\.email\s*\}\}/g, sampleData.offer.customer.email);
    rendered = rendered.replace(/\{\{\s*offer\.customer\.phone\s*\}\}/g, sampleData.offer.customer.phone);
    rendered = rendered.replace(/\{\{\s*offer\.customer\.street\s*\}\}/g, sampleData.offer.customer.street);
    rendered = rendered.replace(/\{\{\s*offer\.customer\.city\s*\}\}/g, sampleData.offer.customer.city);
    rendered = rendered.replace(/\{\{\s*offer\.customer\.postal_code\s*\}\}/g, sampleData.offer.customer.postal_code);
    rendered = rendered.replace(/\{\{\s*offer\.customer\.company_id\s*\}\}/g, sampleData.offer.customer.company_id);
    rendered = rendered.replace(/\{\{\s*offer\.customer\.tax_id\s*\}\}/g, sampleData.offer.customer.tax_id);
    rendered = rendered.replace(/\{\{\s*offer\.contractor\.display_name\s*\}\}/g, sampleData.offer.contractor.display_name);
    rendered = rendered.replace(/\{\{\s*offer\.contractor\.company\.company_name\s*\}\}/g, sampleData.offer.contractor.company.company_name);
    rendered = rendered.replace(/\{\{\s*offer\.project\.name\s*\}\}/g, sampleData.offer.project.name);
    rendered = rendered.replace(/\{\{\s*offer\.project\.location\s*\}\}/g, sampleData.offer.project.location);
    rendered = rendered.replace(/\{\{\s*offer\.project\.start_date\s*\}\}/g, sampleData.offer.project.start_date);
    rendered = rendered.replace(/\{\{\s*offer\.project\.end_date\s*\}\}/g, sampleData.offer.project.end_date);
    rendered = rendered.replace(/\{\{\s*project\.name\s*\}\}/g, sampleData.project.name);
    rendered = rendered.replace(/\{\{\s*project\.id\s*\}\}/g, sampleData.project.id);
    rendered = rendered.replace(/\{\{\s*project\.location\s*\}\}/g, sampleData.project.location);
    rendered = rendered.replace(/\{\{\s*subtotal\s*\}\}/g, sampleData.subtotal);
    rendered = rendered.replace(/\{\{\s*discount_amount\s*\}\}/g, sampleData.discount_amount);
    rendered = rendered.replace(/\{\{\s*vat_amount\s*\}\}/g, sampleData.vat_amount);
    rendered = rendered.replace(/\{\{\s*total\s*\}\}/g, sampleData.total);

    // Handle simple for loops with more flexible regex
    const forLoopRegex = /\{%\s*for\s+item\s+in\s+project_items\s*%\}([\s\S]*?)\{%\s*endfor\s*%\}/gi;

    // Count matches for debugging
    const matches = rendered.match(forLoopRegex);
    if (matches) {
          }

    rendered = rendered.replace(forLoopRegex, (match, loopContent) => {
            const result = sampleData.project_items.map(item => {
        let itemHtml = loopContent;
        // Replace all item properties
        itemHtml = itemHtml.replace(/\{\{\s*item\.name\s*\}\}/g, item.name);
        itemHtml = itemHtml.replace(/\{\{\s*item\.quantity\s*\}\}/g, item.quantity);
        itemHtml = itemHtml.replace(/\{\{\s*item\.price\s*\}\}/g, item.price);
        itemHtml = itemHtml.replace(/\{\{\s*item\.category\s*\}\}/g, item.category);
        itemHtml = itemHtml.replace(/\{\{\s*item\.time_units\s*\}\}/g, item.time_units);
        itemHtml = itemHtml.replace(/\{\{\s*item\.rate_type\s*\}\}/g, item.rate_type);
        itemHtml = itemHtml.replace(/\{\{\s*item\.price_rent\s*\}\}/g, item.price_rent);
        return itemHtml;
      }).join('');
            return result;
    });

    // Remove remaining Jinja2 tags for cleaner preview
    rendered = rendered.replace(/\{%\s*if\s+.*?\s*%\}/g, '');
    rendered = rendered.replace(/\{%\s*endif\s*%\}/g, '');
    rendered = rendered.replace(/\{%\s*else\s*%\}/g, '');

    return rendered;
  };

  useEffect(() => {
    setTemplateContent(initialTemplate);
  }, [initialTemplate]);

  useEffect(() => {
    // Update visual editor when switching to WYSIWYG mode or when template changes
    // BUT NOT during drag-drop or undo-redo operations
    if (editorMode === 'wysiwyg' && editorRef.current && !isUndoRedoAction && !isDragDropAction) {
      const renderedContent = renderJinja ? renderJinjaVariables(templateContent) : templateContent;
      const sanitizedContent = DOMPurify.sanitize(renderedContent);
      if (editorRef.current.innerHTML !== sanitizedContent) {
        editorRef.current.innerHTML = sanitizedContent;
      }
    }
  }, [editorMode, templateContent, renderJinja]);

  // Update editor when renderJinja toggle changes
  useEffect(() => {
    if (editorMode === 'wysiwyg' && editorRef.current && !isDragDropAction) {
      const renderedContent = renderJinja ? renderJinjaVariables(templateContent) : templateContent;
      editorRef.current.innerHTML = DOMPurify.sanitize(renderedContent);
    }
  }, [renderJinja]);

  // Re-render with Jinja2 after drag-drop operation completes
  useEffect(() => {
    if (editorMode === 'wysiwyg' && !isDragDropAction && editorRef.current && renderJinja) {
      const renderedContent = renderJinjaVariables(templateContent);
      editorRef.current.innerHTML = DOMPurify.sanitize(renderedContent);
    }
  }, [isDragDropAction]);

  // Inject Bootstrap CSS and custom styles into the WYSIWYG editor
  useEffect(() => {
    if (editorMode === 'wysiwyg' && editorRef.current) {
      // Create a style element if it doesn't exist
      const editorContainer = editorRef.current.parentElement;
      let styleEl = editorContainer.querySelector('#wysiwyg-styles');

      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'wysiwyg-styles';
        editorContainer.insertBefore(styleEl, editorRef.current);
      }

      // Add Bootstrap-like grid system and common styles
      styleEl.textContent = `
        /* Bootstrap-like styles for WYSIWYG */
        #wysiwyg-editor {
          box-sizing: border-box !important;
          overflow: visible !important;
        }

        #wysiwyg-editor * {
          box-sizing: border-box !important;
          max-width: 100% !important;
        }

        #wysiwyg-editor img {
          max-width: 100% !important;
          height: auto !important;
          display: block;
        }

        #wysiwyg-editor table {
          width: 100% !important;
          max-width: 100% !important;
          table-layout: fixed !important;
          word-wrap: break-word !important;
        }

        #wysiwyg-editor .container {
          width: 100% !important;
          max-width: 100% !important;
          padding-right: 15px;
          padding-left: 15px;
          margin-right: auto;
          margin-left: auto;
        }

        #wysiwyg-editor .row {
          display: flex;
          flex-wrap: wrap;
          margin-right: -15px;
          margin-left: -15px;
          max-width: 100% !important;
        }
        #wysiwyg-editor .row {
          display: flex;
          flex-wrap: wrap;
          margin-right: -15px;
          margin-left: -15px;
        }
        #wysiwyg-editor .col-1 { flex: 0 0 8.333333%; max-width: 8.333333%; padding: 0 15px; }
        #wysiwyg-editor .col-2 { flex: 0 0 16.666667%; max-width: 16.666667%; padding: 0 15px; }
        #wysiwyg-editor .col-3 { flex: 0 0 25%; max-width: 25%; padding: 0 15px; }
        #wysiwyg-editor .col-4 { flex: 0 0 33.333333%; max-width: 33.333333%; padding: 0 15px; }
        #wysiwyg-editor .col-5 { flex: 0 0 41.666667%; max-width: 41.666667%; padding: 0 15px; }
        #wysiwyg-editor .col-6 { flex: 0 0 50%; max-width: 50%; padding: 0 15px; }
        #wysiwyg-editor .col-7 { flex: 0 0 58.333333%; max-width: 58.333333%; padding: 0 15px; }
        #wysiwyg-editor .col-8 { flex: 0 0 66.666667%; max-width: 66.666667%; padding: 0 15px; }
        #wysiwyg-editor .col-9 { flex: 0 0 75%; max-width: 75%; padding: 0 15px; }
        #wysiwyg-editor .col-10 { flex: 0 0 83.333333%; max-width: 83.333333%; padding: 0 15px; }
        #wysiwyg-editor .col-11 { flex: 0 0 91.666667%; max-width: 91.666667%; padding: 0 15px; }
        #wysiwyg-editor .col-12 { flex: 0 0 100%; max-width: 100%; padding: 0 15px; }

        /* Spacing utilities */
        #wysiwyg-editor .mb-1 { margin-bottom: 0.25rem; }
        #wysiwyg-editor .mb-2 { margin-bottom: 0.5rem; }
        #wysiwyg-editor .mb-3 { margin-bottom: 1rem; }
        #wysiwyg-editor .mb-4 { margin-bottom: 1.5rem; }
        #wysiwyg-editor .mb-5 { margin-bottom: 3rem; }
        #wysiwyg-editor .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
        #wysiwyg-editor .p-1 { padding: 0.25rem; }

        /* Flexbox utilities */
        #wysiwyg-editor .d-flex { display: flex; }
        #wysiwyg-editor .flex-column { flex-direction: column; }
        #wysiwyg-editor .justify-content-center { justify-content: center; }
        #wysiwyg-editor .justify-content-between { justify-content: space-between; }
        #wysiwyg-editor .justify-content-end { justify-content: flex-end; }
        #wysiwyg-editor .align-items-center { align-items: center; }
        #wysiwyg-editor .align-items-end { align-items: flex-end; }
        #wysiwyg-editor .gap-1 { gap: 0.25rem; }

        /* Text utilities */
        #wysiwyg-editor .text-center { text-align: center; }
        #wysiwyg-editor .text-end { text-align: right; }
        #wysiwyg-editor .text-danger { color: #dc3545; }
        #wysiwyg-editor .fw-bold { font-weight: bold; }
        #wysiwyg-editor strong { font-weight: bold; }

        /* Position utilities */
        #wysiwyg-editor .position-relative { position: relative; }
        #wysiwyg-editor .position-absolute { position: absolute; }

        /* Image utilities */
        #wysiwyg-editor .img-fluid { max-width: 100%; height: auto; }

        /* Width utilities */
        #wysiwyg-editor .w-100 { width: 100%; }

        /* Margins */
        #wysiwyg-editor .m-0 { margin: 0; }
        #wysiwyg-editor .my-1 { margin-top: 0.25rem; margin-bottom: 0.25rem; }

        /* Common element styles */
        #wysiwyg-editor h1 { font-size: 2rem; margin: 0.5rem 0; }
        #wysiwyg-editor p { margin: 0.5rem 0; }
        #wysiwyg-editor table { width: 100%; border-collapse: collapse; }
        #wysiwyg-editor th, #wysiwyg-editor td { padding: 0.5rem; }

        /* Template variable highlighting */
        #wysiwyg-editor .template-variable {
          background-color: #e3f2fd;
          padding: 2px 6px;
          border-radius: 3px;
          font-family: monospace;
          font-size: 0.9em;
          border: 1px dashed #2196f3;
        }

        /* Preserve inline styles */
        #wysiwyg-editor [style] {
          /* Inline styles will be preserved */
        }
      `;
    }
  }, [editorMode]);

  const handleSave = () => {
    try {
      // IMPORTANT: Always save the original templateContent (with Jinja2 syntax)
      // NOT the rendered HTML from the editor when preview mode is ON
      let contentToSave = templateContent;

      // Only get content from editor if:
      // 1. In WYSIWYG mode AND
      // 2. Preview mode is OFF (otherwise editor shows rendered HTML)
      if (editorMode === 'wysiwyg' && editorRef.current && !renderJinja) {
        contentToSave = editorRef.current.innerHTML;
      }

      // CRITICAL: Decode HTML entities to prevent Django template parsing errors
      // contentEditable may encode characters like > to &gt;, < to &lt;, etc.
      const textarea = document.createElement('textarea');
      textarea.innerHTML = contentToSave;
      contentToSave = textarea.value;

      // Basic validation
      if (templateType === 'custom' && !contentToSave.trim()) {
        setError(t('projects.offers.templates.emptyTemplate'));
        return;
      }


      onSave(contentToSave);
      onHide();
      setError(null);
    } catch (err) {
      setError(t('projects.offers.templates.saveError'));
      console.error('Error saving template:', err);
    }
  };

  const handleWysiwygInput = () => {
    if (editorRef.current && !isUndoRedoAction) {
      const newContent = editorRef.current.innerHTML;
      setTemplateContent(newContent);
      saveToHistory(newContent);
    }
  };

  // Save current state to history for undo/redo
  const saveToHistory = (content) => {
    setHistory(prev => {
      // Remove any future history if we're not at the end
      const newHistory = prev.slice(0, historyIndex + 1);
      // Add new state
      newHistory.push(content);
      // Limit history to 50 items
      if (newHistory.length > 50) {
        newHistory.shift();
        return newHistory;
      }
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  };

  // Undo function
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousContent = history[newIndex];
      setIsUndoRedoAction(true);
      setTemplateContent(previousContent);
      setHistoryIndex(newIndex);

      if (editorRef.current) {
        editorRef.current.innerHTML = DOMPurify.sanitize(previousContent);
      }

      setTimeout(() => setIsUndoRedoAction(false), 100);
    }
  };

  // Redo function
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextContent = history[newIndex];
      setIsUndoRedoAction(true);
      setTemplateContent(nextContent);
      setHistoryIndex(newIndex);

      if (editorRef.current) {
        editorRef.current.innerHTML = DOMPurify.sanitize(nextContent);
      }

      setTimeout(() => setIsUndoRedoAction(false), 100);
    }
  };

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };

    if (show && editorMode === 'wysiwyg') {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [show, editorMode, historyIndex, history]);

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const insertVariable = (variable) => {
    if (editorMode === 'wysiwyg' && editorRef.current) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const span = document.createElement('span');
        span.className = 'template-variable';
        span.style.backgroundColor = '#e3f2fd';
        span.style.padding = '2px 6px';
        span.style.borderRadius = '3px';
        span.style.fontFamily = 'monospace';
        span.style.fontSize = '0.9em';
        span.textContent = variable;
        range.insertNode(span);
        range.setStartAfter(span);
        range.setEndAfter(span);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        editorRef.current.innerHTML += DOMPurify.sanitize(
          `<span class="template-variable" style="background-color: #e3f2fd; padding: 2px 6px; border-radius: 3px; font-family: monospace; font-size: 0.9em;">${variable}</span>`,
          { ALLOWED_TAGS: ['span'], ALLOWED_ATTR: ['class', 'style'] }
        );
      }
      handleWysiwygInput();
    } else {
      setTemplateContent(prev => prev + variable);
    }
  };

  const insertTable = () => {
    const tableHtml = `
<table style="width: 100%; border-collapse: collapse; margin: 10px 0;">
  <thead>
    <tr>
      <th style="border: 1px solid #ddd; padding: 8px; background-color: #f4f4f4;">Column 1</th>
      <th style="border: 1px solid #ddd; padding: 8px; background-color: #f4f4f4;">Column 2</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td style="border: 1px solid #ddd; padding: 8px;">Data 1</td>
      <td style="border: 1px solid #ddd; padding: 8px;">Data 2</td>
    </tr>
  </tbody>
</table>`;

    if (editorMode === 'wysiwyg' && editorRef.current) {
      editorRef.current.innerHTML += DOMPurify.sanitize(tableHtml);
      handleWysiwygInput();
    }
  };

  const changeTextColor = () => {
    const color = prompt('Enter color (e.g., #ff0000, red, rgb(255,0,0)):');
    if (color) {
      execCommand('foreColor', color);
    }
  };

  const changeBackgroundColor = () => {
    const color = prompt('Enter background color (e.g., #ffff00, yellow):');
    if (color) {
      execCommand('backColor', color);
    }
  };

  const changeFontSize = (size) => {
    execCommand('fontSize', size);
  };

  // Component Library - Pre-built components with REAL Jinja2 variables
  const componentLibrary = [
    {
      id: 'header',
      name: t('projects.offers.templates.components.header') || 'Header Section',
      icon: '📋',
      html: `<div class="mb-3" style="background-color: #4CAF50; padding: 20px; text-align: center;">
  <h1 style="color: white; margin: 0;">{{ offer.name }}</h1>
  <p style="color: white; margin: 5px 0;">Číslo nabídky: {{ offer.id }}</p>
  <p style="color: white; margin: 5px 0;">Platnost: {{ offer.offer_valid_until }}</p>
</div>`
    },
    {
      id: 'customer-info',
      name: t('projects.offers.templates.components.customerInfo') || 'Customer Info',
      icon: '👤',
      html: `<div class="mb-3" style="padding: 15px; border: 2px solid #0066cc; border-radius: 5px; background-color: #f8f9fa;">
  <h3 style="color: #0066cc; margin-top: 0;">Zákazník</h3>
  <p style="margin: 5px 0;"><strong>{{ offer.customer.company_name }}</strong></p>
  <p style="margin: 5px 0;">Kontakt: {{ offer.customer.resp_person }}</p>
  <p style="margin: 5px 0;">Email: {{ offer.customer.email }}</p>
  <p style="margin: 5px 0;">Tel: {{ offer.customer.phone }}</p>
  <p style="margin: 5px 0;">Adresa: {{ offer.customer.street }}, {{ offer.customer.city }} {{ offer.customer.postal_code }}</p>
</div>`
    },
    {
      id: 'project-info',
      name: t('projects.offers.templates.components.projectInfo') || 'Project Info',
      icon: '📁',
      html: `<div class="mb-3" style="padding: 15px; background-color: #e3f2fd; border-left: 4px solid #2196f3;">
  <h3 style="color: #1976d2; margin-top: 0;">Projekt</h3>
  <p style="margin: 5px 0;"><strong>{{ offer.project.name }}</strong></p>
  <p style="margin: 5px 0;">Místo: {{ offer.project.location }}</p>
  <p style="margin: 5px 0;">Od: {{ offer.project.start_date }} | Do: {{ offer.project.end_date }}</p>
</div>`
    },
    {
      id: 'two-column',
      name: t('projects.offers.templates.components.twoColumn') || 'Two Columns',
      icon: '⚏',
      html: `<div class="container mb-3">
  <div class="row">
    <div class="col-6">
      <h3>Zákazník</h3>
      <p>{{ offer.customer.company_name }}</p>
      <p>{{ offer.customer.resp_person }}</p>
    </div>
    <div class="col-6">
      <h3>Dodavatel</h3>
      <p>{{ offer.contractor.company.company_name }}</p>
      <p>{{ offer.contractor.display_name }}</p>
    </div>
  </div>
</div>`
    },
    {
      id: 'financial-summary',
      name: t('projects.offers.templates.components.financialSummary') || 'Financial Summary',
      icon: '💰',
      html: `<div class="mb-3" style="padding: 20px; background-color: #f1f8e9; border: 2px solid #8bc34a; border-radius: 5px;">
  <h3 style="color: #558b2f; margin-top: 0;">Finanční souhrn</h3>
  <table style="width: 100%; border-collapse: collapse;">
    <tr>
      <td style="padding: 5px 0;">Mezisoučet:</td>
      <td style="padding: 5px 0; text-align: right;"><strong>{{ subtotal }} {{ offer.currency }}</strong></td>
    </tr>
    <tr>
      <td style="padding: 5px 0;">Sleva ({{ offer.discount }}%):</td>
      <td style="padding: 5px 0; text-align: right; color: #d32f2f;">-{{ discount_amount }} {{ offer.currency }}</td>
    </tr>
    <tr>
      <td style="padding: 5px 0;">DPH (21%):</td>
      <td style="padding: 5px 0; text-align: right;">{{ vat_amount }} {{ offer.currency }}</td>
    </tr>
    <tr style="border-top: 2px solid #558b2f;">
      <td style="padding: 10px 0;"><strong>Celkem:</strong></td>
      <td style="padding: 10px 0; text-align: right;"><strong style="font-size: 18px; color: #558b2f;">{{ total }} {{ offer.currency }}</strong></td>
    </tr>
  </table>
</div>`
    },
    {
      id: 'items-table',
      name: t('projects.offers.templates.components.itemsTable') || 'Items Table',
      icon: '📊',
      html: `<div class="mb-3">
  <h3>Položky nabídky</h3>
  <table style="width: 100%; border-collapse: collapse; margin: 10px 0;">
    <thead>
      <tr>
        <th style="border: 1px solid #ddd; padding: 8px; background-color: #f4f4f4; text-align: left;">Položka</th>
        <th style="border: 1px solid #ddd; padding: 8px; background-color: #f4f4f4; text-align: center;">Množství</th>
        <th style="border: 1px solid #ddd; padding: 8px; background-color: #f4f4f4; text-align: center;">Kategorie</th>
        <th style="border: 1px solid #ddd; padding: 8px; background-color: #f4f4f4; text-align: right;">Cena</th>
      </tr>
    </thead>
    <tbody>
      {% for item in project_items %}
      <tr>
        <td style="border: 1px solid #ddd; padding: 8px;">{{ item.name }}</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">{{ item.quantity }}</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">{{ item.category }}</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">{{ item.price }} Kč</td>
      </tr>
      {% endfor %}
    </tbody>
  </table>
</div>`
    },
    {
      id: 'items-cards',
      name: t('projects.offers.templates.components.itemsCards') || 'Items as Cards',
      icon: '🔄',
      html: `<div class="mb-3">
  <h3>Položky projektu</h3>
  {% for item in project_items %}
  <div style="padding: 15px; margin: 10px 0; border: 1px solid #ddd; border-radius: 5px; background-color: #fafafa;">
    <div style="display: flex; justify-content: space-between; align-items: center;">
      <div>
        <strong style="font-size: 16px; color: #333;">{{ item.name }}</strong>
        <p style="margin: 5px 0; color: #666;">Kategorie: {{ item.category }}</p>
        <p style="margin: 5px 0; color: #666;">Množství: {{ item.quantity }} ks</p>
      </div>
      <div style="text-align: right;">
        <strong style="font-size: 18px; color: #4CAF50;">{{ item.price }} Kč</strong>
      </div>
    </div>
  </div>
  {% endfor %}
</div>`
    },
    {
      id: 'offer-details',
      name: t('projects.offers.templates.components.offerDetails') || 'Offer Details',
      icon: '📋',
      html: `<div class="mb-3" style="padding: 15px; border: 1px solid #ddd; background-color: white;">
  <h3 style="margin-top: 0;">Detaily nabídky</h3>
  <table style="width: 100%;">
    <tr>
      <td style="padding: 5px 0; font-weight: bold;">Číslo nabídky:</td>
      <td style="padding: 5px 0;">{{ offer.id }}</td>
    </tr>
    <tr>
      <td style="padding: 5px 0; font-weight: bold;">Datum vystavení:</td>
      <td style="padding: 5px 0;">{{ offer.date_issued }}</td>
    </tr>
    <tr>
      <td style="padding: 5px 0; font-weight: bold;">Platnost do:</td>
      <td style="padding: 5px 0;">{{ offer.offer_valid_until }}</td>
    </tr>
    <tr>
      <td style="padding: 5px 0; font-weight: bold;">Verze:</td>
      <td style="padding: 5px 0;">{{ offer.version }}</td>
    </tr>
    {% if offer.note %}
    <tr>
      <td style="padding: 5px 0; font-weight: bold;">Poznámka:</td>
      <td style="padding: 5px 0;">{{ offer.note }}</td>
    </tr>
    {% endif %}
  </table>
</div>`
    },
    {
      id: 'note-box',
      name: t('projects.offers.templates.components.noteBox') || 'Note Box',
      icon: '📝',
      html: `{% if offer.note %}
<div style="background-color: #fff3cd; border-left: 4px solid #ffc107; padding: 15px; margin: 15px 0;">
  <strong style="color: #856404;">Poznámka:</strong>
  <p style="margin: 5px 0; color: #856404;">{{ offer.note }}</p>
</div>
{% endif %}`
    },
    {
      id: 'discount-highlight',
      name: t('projects.offers.templates.components.discountHighlight') || 'Discount Highlight',
      icon: '🏷️',
      html: `{% if offer.discount %}
<div style="background-color: #ffebee; border: 2px solid #f44336; padding: 20px; margin: 15px 0; text-align: center; border-radius: 10px;">
  <h2 style="color: #c62828; margin: 0;">🎉 SLEVA {{ offer.discount }}% 🎉</h2>
  <p style="margin: 10px 0; font-size: 18px; color: #d32f2f;">Ušetříte {{ discount_amount }} Kč!</p>
</div>
{% endif %}`
    },
    {
      id: 'footer',
      name: t('projects.offers.templates.components.footer') || 'Footer',
      icon: '📄',
      html: `<div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 2px solid #333;">
  <p style="font-size: 16px; margin: 10px 0;">Děkujeme za Váš zájem!</p>
  <p style="font-size: 13px; color: #666; margin: 5px 0;">{{ offer.contractor.company.company_name }}</p>
  <p style="font-size: 13px; color: #666; margin: 5px 0;">{{ offer.contractor.display_name }}</p>
</div>`
    },
    {
      id: 'spacer',
      name: t('projects.offers.templates.components.spacer') || 'Spacer',
      icon: '␣',
      html: `<div style="height: 30px;"></div>`
    },
    {
      id: 'divider',
      name: t('projects.offers.templates.components.divider') || 'Divider',
      icon: '─',
      html: `<hr style="border: 0; border-top: 2px solid #333; margin: 20px 0;">`
    }
  ];

  // Handle drag start
  const handleDragStart = (e, component) => {
    e.dataTransfer.setData('text/html', component.html);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Handle drop in editor
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDraggingOver(false);

    const html = e.dataTransfer.getData('text/html');

    if (html && editorRef.current) {
      setIsDragDropAction(true);

      // Get the drop position - cross-browser compatible
      let range = null;

      // Try different methods for different browsers
      if (document.caretRangeFromPoint) {
        // Chrome, Safari
        range = document.caretRangeFromPoint(e.clientX, e.clientY);
      } else if (document.caretPositionFromPoint) {
        // Firefox
        const position = document.caretPositionFromPoint(e.clientX, e.clientY);
        if (position) {
          range = document.createRange();
          range.setStart(position.offsetNode, position.offset);
          range.collapse(true);
        }
      }

      const selection = window.getSelection();

      if (range) {
        // Clear any existing selection
        selection.removeAllRanges();
        selection.addRange(range);

        // Insert the HTML at the drop point
        const fragment = range.createContextualFragment(html);
        range.deleteContents();
        range.insertNode(fragment);

        // Move cursor after inserted content
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // Fallback: append at the end if position detection fails
        editorRef.current.innerHTML += DOMPurify.sanitize(html);
      }

      // Get the new content and save
      const newContent = editorRef.current.innerHTML;
      setTemplateContent(newContent);
      saveToHistory(newContent);

      // Reset flag after a delay
      setTimeout(() => setIsDragDropAction(false), 100);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
  };

  const defaultTemplate = `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>{{ offer.name }}</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 40px;
            line-height: 1.6;
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
        }
        .section {
            margin-bottom: 20px;
        }
        .section-title {
            font-weight: bold;
            font-size: 18px;
            margin-bottom: 10px;
            border-bottom: 2px solid #333;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
        }
        th, td {
            padding: 8px;
            text-align: left;
            border: 1px solid #ddd;
        }
        th {
            background-color: #f4f4f4;
        }
        .total {
            font-weight: bold;
            font-size: 16px;
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>{{ offer.name }}</h1>
        <p>Číslo nabídky: {{ offer.id }}</p>
        <p>Datum vystavení: {{ offer.date_issued }}</p>
        <p>Platnost do: {{ offer.offer_valid_until }}</p>
    </div>

    {% if offer.include_customer_info and offer.customer %}
    <div class="section">
        <div class="section-title">Zákazník</div>
        <p>{{ offer.customer.company_name }}</p>
        <p>{{ offer.customer.resp_person }}</p>
        <p>{{ offer.customer.email }}</p>
        <p>{{ offer.customer.phone }}</p>
    </div>
    {% endif %}

    {% if offer.description %}
    <div class="section">
        <div class="section-title">Popis</div>
        <p>{{ offer.description }}</p>
    </div>
    {% endif %}

    {% if offer.include_items %}
    <div class="section">
        <div class="section-title">Položky</div>
        <table>
            <thead>
                <tr>
                    <th>Položka</th>
                    <th>Množství</th>
                    {% if not offer.hide_prices %}
                    <th>Cena za jednotku</th>
                    <th>Celkem</th>
                    {% endif %}
                </tr>
            </thead>
            <tbody>
                {% for item in project_items %}
                <tr>
                    <td>{{ item.name }}</td>
                    <td>{{ item.quantity }}</td>
                    {% if not offer.hide_prices %}
                    <td>{{ item.price }} Kč</td>
                    <td>{{ item.total_price }} Kč</td>
                    {% endif %}
                </tr>
                {% endfor %}
            </tbody>
        </table>
    </div>
    {% endif %}

    {% if not offer.hide_prices %}
    <div class="section">
        <div class="section-title">Celková cena</div>
        <table>
            <tr>
                <td>Mezisoučet:</td>
                <td class="total">{{ subtotal }} Kč</td>
            </tr>
            {% if offer.discount > 0 %}
            <tr>
                <td>Sleva ({{ offer.discount }}%):</td>
                <td class="total">-{{ discount_amount }} Kč</td>
            </tr>
            {% endif %}
            {% if offer.include_vat %}
            <tr>
                <td>DPH (21%):</td>
                <td class="total">{{ vat_amount }} Kč</td>
            </tr>
            {% endif %}
            <tr>
                <td><strong>Celkem:</strong></td>
                <td class="total">{{ total }} Kč</td>
            </tr>
        </table>
    </div>
    {% endif %}

    {% if offer.note %}
    <div class="section">
        <div class="section-title">Poznámka</div>
        <p>{{ offer.note }}</p>
    </div>
    {% endif %}

    <div class="section">
        <p style="text-align: center; margin-top: 40px;">
            Děkujeme za Váš zájem!
        </p>
    </div>
</body>
</html>`;

  const insertTemplate = () => {
    setTemplateContent(defaultTemplate);
  };

  const availableVariables = [
    { var: '{{ offer.name }}', desc: 'Název nabídky' },
    { var: '{{ offer.id }}', desc: 'ID nabídky' },
    { var: '{{ offer.date_issued }}', desc: 'Datum vystavení' },
    { var: '{{ offer.offer_valid_until }}', desc: 'Platnost do' },
    { var: '{{ offer.version }}', desc: 'Verze' },
    { var: '{{ offer.discount }}', desc: 'Sleva (%)' },
    { var: '{{ offer.note }}', desc: 'Poznámka' },
    { var: '{{ offer.description }}', desc: 'Popis' },
    { var: '{{ offer.customer.company_name }}', desc: 'Název firmy zákazníka' },
    { var: '{{ offer.customer.resp_person }}', desc: 'Kontaktní osoba' },
    { var: '{{ offer.customer.email }}', desc: 'Email zákazníka' },
    { var: '{{ offer.customer.phone }}', desc: 'Telefon zákazníka' },
    { var: '{{ offer.contractor.display_name }}', desc: 'Jméno dodavatele' },
    { var: '{{ offer.project.name }}', desc: 'Název projektu' },
    { var: '{{ subtotal }}', desc: 'Mezisoučet' },
    { var: '{{ discount_amount }}', desc: 'Částka slevy' },
    { var: '{{ vat_amount }}', desc: 'Částka DPH' },
    { var: '{{ total }}', desc: 'Celková cena' },
  ];

  return (
    <>
      {/* Dark mode styles for component library and toolbar using Tabler variables */}
      <style>{`
        /* Component Library Sidebar - uses Tabler theme colors */
        .component-library-sidebar {
          background-color: var(--tblr-bg-surface, #f8f9fa);
          border-color: var(--tblr-border-color, #dee2e6) !important;
        }

        .component-library-sidebar h6,
        .component-library-sidebar p {
          color: var(--tblr-body-color, #1e293b);
        }

        .component-library-item {
          background-color: var(--tblr-bg-forms, #ffffff);
          border-color: var(--tblr-border-color, #dee2e6) !important;
          color: var(--tblr-body-color, #1e293b);
        }

        .component-library-item:hover {
          background-color: var(--tblr-bg-hover, #f1f5f9);
        }

        .wysiwyg-toolbar {
          background-color: var(--tblr-bg-surface, #f8f9fa);
          border-color: var(--tblr-border-color, #dee2e6) !important;
        }

        .wysiwyg-toolbar .text-muted {
          color: var(--tblr-body-color-secondary, #6c757d) !important;
        }

        .wysiwyg-editor-container {
          background-color: var(--tblr-bg-surface-secondary, #f5f5f5);
        }

        /* Keep editor page WHITE for accurate preview - always white regardless of theme */
        .wysiwyg-editor-page {
          background-color: white !important;
          color: #000 !important;
        }
      `}</style>

      <Modal show={show} onHide={onHide} size="xl" fullscreen="lg-down">
      <Modal.Header closeButton>
        <Modal.Title>
          {t('projects.offers.templates.editorTitle')}
          <ButtonGroup size="sm" className="ms-3">
            <Button
              variant={editorMode === 'wysiwyg' ? 'primary' : 'outline-primary'}
              onClick={() => setEditorMode('wysiwyg')}
            >
              <i className="ti ti-eye me-1"></i>
              {t('projects.offers.templates.visualMode')}
            </Button>
            <Button
              variant={editorMode === 'code' ? 'primary' : 'outline-primary'}
              onClick={() => setEditorMode('code')}
            >
              <i className="ti ti-code me-1"></i>
              {t('projects.offers.templates.codeMode')}
            </Button>
          </ButtonGroup>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        <div className="d-flex gap-2 mb-2">
          <Form.Check
            type="switch"
            id="toggle-component-library"
            label={t('projects.offers.templates.showComponents') || 'Show Components'}
            checked={showComponentLibrary}
            onChange={(e) => setShowComponentLibrary(e.target.checked)}
          />
          {editorMode === 'wysiwyg' && (
            <Form.Check
              type="switch"
              id="toggle-jinja-render"
              label={t('projects.offers.templates.previewMode') || 'Preview with Data'}
              checked={renderJinja}
              onChange={(e) => setRenderJinja(e.target.checked)}
            />
          )}
        </div>

        <div className="d-flex flex-column flex-md-row gap-2">
          {/* Component Library Sidebar */}
          {showComponentLibrary && editorMode === 'wysiwyg' && (
            <div style={{ width: '100%', maxWidth: '250px', flexShrink: 0, maxHeight: '650px', overflowY: 'auto' }}>
              <div className="border rounded p-2 component-library-sidebar" style={{ height: '100%' }}>
                <h6 className="mb-2 sticky-top pb-2 component-library-sidebar" style={{ borderBottom: '1px solid #dee2e6' }}>
                  <i className="ti ti-components me-1"></i>
                  {t('projects.offers.templates.componentLibrary') || 'Components'}
                </h6>
                <p className="text-muted small mb-2">
                  {t('projects.offers.templates.dragToAdd') || 'Drag to editor'}
                </p>
                <div className="d-flex flex-column gap-2">
                  {componentLibrary.map((component) => (
                    <div
                      key={component.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, component)}
                      className="border rounded p-2 component-library-item"
                      style={{
                        cursor: 'grab',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                    >
                      <div className="d-flex align-items-center">
                        <span style={{ fontSize: '24px', marginRight: '8px' }}>{component.icon}</span>
                        <span style={{ fontSize: '13px', fontWeight: '500' }}>{component.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Editor Area */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
          <Tab eventKey="wysiwyg" title={t('projects.offers.templates.editor')}>
            {editorMode === 'wysiwyg' ? (
              <>
                {/* WYSIWYG Toolbar */}
                <div className="border rounded p-2 mb-2 wysiwyg-toolbar" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <div className="d-flex flex-wrap gap-1">
                    {/* Undo/Redo */}
                    <ButtonGroup size="sm">
                      <Button
                        variant="outline-secondary"
                        onClick={handleUndo}
                        disabled={historyIndex === 0}
                        title={t('projects.offers.templates.undo') || 'Undo (Ctrl+Z)'}
                      >
                        <i className="ti ti-arrow-back-up"></i>
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={handleRedo}
                        disabled={historyIndex >= history.length - 1}
                        title={t('projects.offers.templates.redo') || 'Redo (Ctrl+Y)'}
                      >
                        <i className="ti ti-arrow-forward-up"></i>
                      </Button>
                    </ButtonGroup>

                    <ButtonGroup size="sm">
                      <Button variant="outline-secondary" onClick={() => execCommand('bold')} title="Bold (Ctrl+B)">
                        <strong>B</strong>
                      </Button>
                      <Button variant="outline-secondary" onClick={() => execCommand('italic')} title="Italic (Ctrl+I)">
                        <em>I</em>
                      </Button>
                      <Button variant="outline-secondary" onClick={() => execCommand('underline')} title="Underline (Ctrl+U)">
                        <u>U</u>
                      </Button>
                      <Button variant="outline-secondary" onClick={() => execCommand('strikeThrough')} title="Strikethrough">
                        <s>S</s>
                      </Button>
                    </ButtonGroup>

                    <ButtonGroup size="sm">
                      <Button variant="outline-secondary" onClick={() => changeFontSize('1')} title="Small">
                        <span style={{ fontSize: '10px' }}>A</span>
                      </Button>
                      <Button variant="outline-secondary" onClick={() => changeFontSize('3')} title="Normal">
                        <span style={{ fontSize: '13px' }}>A</span>
                      </Button>
                      <Button variant="outline-secondary" onClick={() => changeFontSize('5')} title="Large">
                        <span style={{ fontSize: '16px' }}>A</span>
                      </Button>
                    </ButtonGroup>

                    <ButtonGroup size="sm">
                      <Button variant="outline-secondary" onClick={() => execCommand('formatBlock', '<h1>')} title="Heading 1">
                        H1
                      </Button>
                      <Button variant="outline-secondary" onClick={() => execCommand('formatBlock', '<h2>')} title="Heading 2">
                        H2
                      </Button>
                      <Button variant="outline-secondary" onClick={() => execCommand('formatBlock', '<h3>')} title="Heading 3">
                        H3
                      </Button>
                      <Button variant="outline-secondary" onClick={() => execCommand('formatBlock', '<p>')} title="Paragraph">
                        P
                      </Button>
                    </ButtonGroup>

                    <ButtonGroup size="sm">
                      <Button variant="outline-secondary" onClick={changeTextColor} title="Text Color">
                        <i className="ti ti-palette" style={{ color: '#333' }}></i>
                      </Button>
                      <Button variant="outline-secondary" onClick={changeBackgroundColor} title="Background Color">
                        <i className="ti ti-color-swatch"></i>
                      </Button>
                    </ButtonGroup>

                    <ButtonGroup size="sm">
                      <Button variant="outline-secondary" onClick={() => execCommand('insertUnorderedList')} title="Bullet List">
                        <i className="ti ti-list"></i>
                      </Button>
                      <Button variant="outline-secondary" onClick={() => execCommand('insertOrderedList')} title="Numbered List">
                        <i className="ti ti-list-numbers"></i>
                      </Button>
                      <Button variant="outline-secondary" onClick={() => execCommand('indent')} title="Indent">
                        <i className="ti ti-indent-increase"></i>
                      </Button>
                      <Button variant="outline-secondary" onClick={() => execCommand('outdent')} title="Outdent">
                        <i className="ti ti-indent-decrease"></i>
                      </Button>
                    </ButtonGroup>

                    <ButtonGroup size="sm">
                      <Button variant="outline-secondary" onClick={() => execCommand('justifyLeft')} title="Align Left">
                        <i className="ti ti-align-left"></i>
                      </Button>
                      <Button variant="outline-secondary" onClick={() => execCommand('justifyCenter')} title="Align Center">
                        <i className="ti ti-align-center"></i>
                      </Button>
                      <Button variant="outline-secondary" onClick={() => execCommand('justifyRight')} title="Align Right">
                        <i className="ti ti-align-right"></i>
                      </Button>
                      <Button variant="outline-secondary" onClick={() => execCommand('justifyFull')} title="Justify">
                        <i className="ti ti-align-justified"></i>
                      </Button>
                    </ButtonGroup>

                    <ButtonGroup size="sm">
                      <Button variant="outline-secondary" onClick={insertTable} title="Insert Table">
                        <i className="ti ti-table"></i>
                      </Button>
                      <Button variant="outline-secondary" onClick={() => execCommand('insertHorizontalRule')} title="Horizontal Line">
                        <i className="ti ti-minus"></i>
                      </Button>
                      <Button variant="outline-secondary" onClick={() => execCommand('removeFormat')} title="Clear Formatting">
                        <i className="ti ti-clear-formatting"></i>
                      </Button>
                    </ButtonGroup>

                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={insertTemplate}
                    >
                      <i className="ti ti-file-import me-1"></i>
                      {t('projects.offers.templates.loadDefault')}
                    </Button>
                  </div>

                  {/* History Status */}
                  {historyIndex > 0 && (
                    <div className="mt-2 text-muted small">
                      <i className="ti ti-history me-1"></i>
                      {t('projects.offers.templates.historyStatus') || 'History'}: {historyIndex} / {history.length - 1}
                      {historyIndex > 0 && (
                        <span className="ms-2 text-info">
                          ({history.length - 1 - historyIndex} {t('projects.offers.templates.changesAhead') || 'changes ahead'})
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* WYSIWYG Editor Container */}
                <div className="wysiwyg-editor-container" style={{
                  width: '100%',
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  maxHeight: '620px',
                  padding: '10px',
                  borderRadius: '4px'
                }}>
                  <div
                    id="wysiwyg-editor"
                    ref={editorRef}
                    contentEditable={!renderJinja}
                    suppressContentEditableWarning
                    onInput={handleWysiwygInput}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className="border rounded wysiwyg-editor-page"
                    style={{
                      minHeight: '580px',
                      fontFamily: 'Arial, sans-serif',
                      lineHeight: '1.6',
                      fontSize: '13px',
                      boxShadow: isDraggingOver ? '0 0 0 3px rgba(66, 153, 225, 0.5)' : '0 2px 8px rgba(0,0,0,0.1)',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      // Simulate A4 page
                      width: '21cm',
                      maxWidth: '100%',
                      margin: '0 auto',
                      padding: '1cm',
                      // Ensure content stays inside
                      boxSizing: 'border-box',
                      position: 'relative',
                      transition: 'box-shadow 0.2s ease',
                      cursor: renderJinja ? 'not-allowed' : 'text',
                      opacity: renderJinja ? 0.95 : 1
                    }}
                  />
                </div>

                <Form.Text className="text-muted mt-2 d-block">
                  <i className="ti ti-info-circle me-1"></i>
                  {renderJinja
                    ? t('projects.offers.templates.previewModeReadOnly') || 'Preview mode is read-only. Turn off "Preview with Data" to edit.'
                    : t('projects.offers.templates.wysiwygHint')
                  }
                </Form.Text>
              </>
            ) : (
              <>
                {/* Code Editor Mode */}
                <div className="mb-3">
                  <ButtonGroup size="sm">
                    <Button variant="outline-secondary" onClick={insertTemplate}>
                      <i className="ti ti-file-import me-1"></i>
                      {t('projects.offers.templates.loadDefault')}
                    </Button>
                    <Button
                      variant="outline-primary"
                      onClick={() => {
                        const textarea = document.getElementById('templateEditor');
                        textarea.select();
                        document.execCommand('copy');
                      }}
                    >
                      <i className="ti ti-copy me-1"></i>
                      {t('projects.offers.templates.copyToClipboard')}
                    </Button>
                  </ButtonGroup>
                </div>

                <Form.Group>
                  <Form.Label>{t('projects.offers.templates.templateContent')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    id="templateEditor"
                    rows={20}
                    value={templateContent}
                    onChange={(e) => setTemplateContent(e.target.value)}
                    style={{
                      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
                      fontSize: '13px',
                      lineHeight: '1.5'
                    }}
                    placeholder={t('projects.offers.templates.placeholder')}
                  />
                  <Form.Text className="text-muted">
                    {t('projects.offers.templates.hint')}
                  </Form.Text>
                </Form.Group>
              </>
            )}
          </Tab>

          <Tab eventKey="variables" title={t('projects.offers.templates.variables')}>
            <div className="alert alert-info">
              <strong>{t('projects.offers.templates.variablesInfo')}</strong>
              <p className="mb-0">
                {editorMode === 'wysiwyg'
                  ? t('projects.offers.templates.variablesDescriptionWysiwyg')
                  : t('projects.offers.templates.variablesDescription')
                }
              </p>
            </div>

            <table className="table table-sm table-hover">
              <thead>
                <tr>
                  <th>{t('projects.offers.templates.variableName')}</th>
                  <th>{t('projects.offers.templates.variableDescription')}</th>
                  <th className="text-center">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {availableVariables.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <code>{item.var}</code>
                    </td>
                    <td>{item.desc}</td>
                    <td className="text-center">
                      <ButtonGroup size="sm">
                        <Button
                          variant="outline-primary"
                          onClick={() => insertVariable(item.var)}
                          title={t('projects.offers.templates.insertVariable')}
                        >
                          <i className="ti ti-plus"></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          onClick={() => {
                            navigator.clipboard.writeText(item.var);
                          }}
                          title={t('projects.offers.templates.clickToCopy')}
                        >
                          <i className="ti ti-copy"></i>
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="alert alert-secondary mt-3">
              <strong>{t('projects.offers.templates.loopsTitle')}</strong>
              <p><code>{'{% for item in project_items %}'}</code> ... <code>{'{% endfor %}'}</code></p>
              <p><strong>{t('projects.offers.templates.conditionsTitle')}</strong></p>
              <p><code>{'{% if offer.include_items %}'}</code> ... <code>{'{% endif %}'}</code></p>
              {editorMode === 'wysiwyg' && (
                <p className="text-muted mb-0">
                  <i className="ti ti-info-circle me-1"></i>
                  {t('projects.offers.templates.loopsNotice')}
                </p>
              )}
            </div>
          </Tab>

          <Tab eventKey="help" title={t('projects.offers.templates.help')}>
            <div className="alert alert-info">
              <h5>{t('projects.offers.templates.helpTitle')}</h5>
              <p>{t('projects.offers.templates.helpDescription')}</p>
            </div>

            <h6>{t('projects.offers.templates.basicStructure')}</h6>
            <pre style={{ backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
{`<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>{{ offer.name }}</title>
    <style>
        /* Your CSS styles here */
    </style>
</head>
<body>
    <!-- Your content here -->
</body>
</html>`}
            </pre>

            <h6 className="mt-3">{t('projects.offers.templates.exampleUsage')}</h6>
            <pre style={{ backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
{`<h1>{{ offer.name }}</h1>
<p>Datum: {{ offer.date_issued }}</p>

{% if offer.customer %}
<div>
    Zákazník: {{ offer.customer.company_name }}
</div>
{% endif %}

<table>
{% for item in project_items %}
    <tr>
        <td>{{ item.name }}</td>
        <td>{{ item.quantity }}</td>
    </tr>
{% endfor %}
</table>`}
            </pre>
          </Tab>
        </Tabs>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="text-muted small">
            {editorMode === 'wysiwyg' && renderJinja ? (
              <span>
                <i className="ti ti-info-circle me-1"></i>
                Ukládá se původní šablona s Jinja2 syntaxí (ne renderovaný náhled)
              </span>
            ) : (
              <span>
                <i className="ti ti-check me-1"></i>
                Připraveno k uložení
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={handleSave} className="ms-2">
              {t('common.save')}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default TemplateEditor;
